import UtilsService from './UtilsService';
import AnalyticsService, {
    AnalyticsEvents,
    GTM_ANALYTICS_EVENTS
} from './AnalyticsService';
import ABTestToolServiceV2 from './ABTestToolService.v2';
import { trackError } from '../utils/error.utils';
import StorageService from './StorageService';
import CookiesService from './CookiesService';

class PageLoadService {
    isUserLoggedIn: boolean = false;

    handlePageAnalytics = () => {
        this.redirectIfLoggedInUser();
        this.handleFirePageViewAnalytics();
    };

    handleFirePageViewAnalytics = () => {
        this.firePageViewAnalytics();
        this.fireSegmentPageView();
    };

    fireSegmentPageView = () => {
        if (this.isUserLoggedIn) {
            return;
        }
        // window.analytics.page and window.analytics.track add the canonical link value
        // to the event context and not window.location.pathname.
        // Since the value of canonical link is only updated after the page is loaded,
        // we need to wait for the page to load before firing the page view event.

        requestAnimationFrame(() => {
            const canonicalLinkHref = document
                .querySelector('link[rel="canonical"]')
                ?.getAttribute('href');

            // Pages that render no canonical link (e.g. noindex pages) report
            // the page view from the current location instead of erroring.
            if (!canonicalLinkHref) {
                this.reportPageView();
                return;
            }

            const canonicalLinkPathname = new URL(canonicalLinkHref).pathname;
            if (
                canonicalLinkPathname === window.location.pathname ||
                canonicalLinkPathname === '/404'
            ) {
                this.reportPageView();
            } else {
                trackError(
                    'canonical link does not match window.location.pathname',
                    {
                        canonicalLinkPathname,
                        windowLocationPathname: window.location.pathname
                    }
                );
            }
        });
    };

    reportPageView = () => {
        AnalyticsService.reportEvent(`${window.location.pathname} Page Viewed`);
        AnalyticsService.pageView();
    };

    firePageViewAnalytics = () => {
        if (this.isUserLoggedIn) {
            AnalyticsService.track(AnalyticsEvents.logged_in_user);
            UtilsService.redirectTo('/app/login');
            return;
        }
        const pageName = UtilsService.getSignupLandingPageFromLocationPath();
        setTimeout(() => {
            AnalyticsService.trackGtm(GTM_ANALYTICS_EVENTS.virtual_page_view, {
                page_name: pageName
            });
        }, 0);

        const abTestData =
            ABTestToolServiceV2?.getVariantPerTestInPage(
                window.location.pathname
            ) || {};

        AnalyticsService.track(
            AnalyticsEvents.page_view + pageName,
            abTestData
        );

        AnalyticsService.track('Page View: Marketing Site', {
            page_name: pageName,
            path: window.location.pathname
        });

        AnalyticsService.track('Gatsby Page View', {
            path: window.location.pathname
        });
    };

    allowRedirect = () => {
        let shouldAllowRedirect = true;
        const { pathname } = window.location;

        const DO_NOT_REDIRECT_LOGGED_IN_USER_PATHNAMES = [
            '/business-personality-test',
            '/free-invoice-templates/hub',
            '/free-invoice-templates/doula',
            '/free-invoice-templates/consultants',
            '/free-invoice-templates/photographers',
            '/free-invoice-templates/graphic-design',
            '/free-invoice-templates/interior-design',
            '/free-invoice-templates/tutors',
            '/free-invoice-templates/cleaners',
            '/photography-contract-templates',
            '/freelance-consulting-contract-template',
            '/templates',
            '/lp/referrals-tos',
            '/lp/premium-program',
            '/lp/promotional-terms',
            '/lp/honeybook-pic-time-terms-of-service',
            '/lp/honeybook-adobe-terms-of-service',
            '/lp/zero-transaction-fees-terms-of-service',
            '/lp/boss-vision',
            '/tools/self-employment-tax-calculator',
            '/email-signature',
            '/invoice-template',
            '/mission-statement',
            '/email-generator',
            '/process-mapping',
            '/client-relationships'
        ];

        shouldAllowRedirect = !DO_NOT_REDIRECT_LOGGED_IN_USER_PATHNAMES.some(
            pathPrefix => pathname && pathname.startsWith(pathPrefix)
        );

        return !UtilsService.isInTemplatePreviewPage() && shouldAllowRedirect;
    };

    redirectIfLoggedInUser = () => {
        const shouldRedirect = this.allowRedirect();

        try {
            if (shouldRedirect && window.localStorage) {
                const jstorage = StorageService.getItem('jStorage');
                const hasUserInJstorage =
                    jstorage?.HB_CURR_USER &&
                    jstorage?.HB_API_VERSION &&
                    jstorage?.HB_AUTH_TOKEN &&
                    jstorage?.HB_AUTH_USER_ID;

                const hasLoggedInFlagCookie =
                    CookiesService.getCookie('hb_is_logged_in') === 'true';

                if (hasUserInJstorage || hasLoggedInFlagCookie) {
                    UtilsService.storeInitialUtms();
                    UtilsService.storeCoupon();
                    this.isUserLoggedIn = true;
                    return;
                }
            }
            this.isUserLoggedIn = false;
        } catch (error) {
            this.isUserLoggedIn = false;
            trackError(error, { message: 'unable to access localStorage' });
        }
    };
}

export default new PageLoadService();
